// components/PlayerIdentityEdit.tsx — post-setup edit surface for
// player identity (gender, drawnTo, age, drawn-to age range).
//
// Mounted from App.tsx like the other account-area overlays
// (Bookshelf, ReferralsPage, AccountSettingsPage). Opened from the
// user-chip menu's "Player identity" item.
//
// Prefilled with the current state.player values; saving routes through
// the same `applyPlayerSetup` engine helper the first-run CharacterSetup
// uses, so legacy saves can fill in the new fields here without losing
// `setupComplete = true`.

import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { applyPlayerSetup, type GameState, type PlayerGender } from "../engine";

type DrawnGender = Exclude<PlayerGender, null>;

interface PlayerIdentityEditProps {
  state: GameState;
  /** Persist a new state. App.tsx wires this to setState + persist(). */
  onSave: (next: GameState) => void;
  onClose: () => void;
}

const GENDER_VALUES: DrawnGender[] = ["female", "male", "non-binary"];

const AGE_MIN = 17;
const AGE_MAX = 90;

function clamp(v: number): number {
  if (!Number.isFinite(v)) return AGE_MIN;
  const i = Math.trunc(v);
  if (i < AGE_MIN) return AGE_MIN;
  if (i > AGE_MAX) return AGE_MAX;
  return i;
}

export default function PlayerIdentityEdit({
  state,
  onSave,
  onClose,
}: PlayerIdentityEditProps) {
  const { t } = useTranslation(["setup", "common"]);
  const GENDERS: Array<{ value: DrawnGender; label: string }> = GENDER_VALUES.map(
    (value) => ({
      value,
      label: t(
        value === "non-binary"
          ? "setup:genders.nonBinary"
          : value === "male"
            ? "setup:genders.male"
            : "setup:genders.female",
      ),
    }),
  );
  const initial = state.player;
  const [gender, setGender] = useState<PlayerGender>(initial.gender ?? null);
  const [drawnTo, setDrawnTo] = useState<DrawnGender[]>(
    initial.drawnTo ? [...initial.drawnTo] : [],
  );
  const [ageText, setAgeText] = useState<string>(
    typeof initial.age === "number" ? String(initial.age) : "",
  );
  const [drawnToAgeMin, setDrawnToAgeMin] = useState<number>(
    typeof initial.drawnToAgeMin === "number"
      ? clamp(initial.drawnToAgeMin)
      : AGE_MIN,
  );
  const [drawnToAgeMax, setDrawnToAgeMax] = useState<number>(
    typeof initial.drawnToAgeMax === "number"
      ? clamp(initial.drawnToAgeMax)
      : AGE_MAX,
  );
  const [ageError, setAgeError] = useState<string | null>(null);

  // Close on Escape, matching the other overlays.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function toggleDrawnTo(g: DrawnGender) {
    setDrawnTo((cur) =>
      cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g],
    );
  }

  function parseAge(s: string): number | null {
    const trimmed = s.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return null;
    const i = Math.trunc(n);
    if (i < AGE_MIN || i > AGE_MAX) return null;
    return i;
  }

  function handleSave() {
    // Age is optional — empty input means "clear my age / no preference."
    // Non-empty but invalid → show inline error and abort.
    const trimmed = ageText.trim();
    let age: number | undefined;
    if (trimmed) {
      const parsed = parseAge(ageText);
      if (parsed === null) {
        setAgeError(
          t("setup:characterSetup.ageOutOfRange", { min: AGE_MIN, max: AGE_MAX }),
        );
        return;
      }
      age = parsed;
    }
    let min = clamp(drawnToAgeMin);
    let max = clamp(drawnToAgeMax);
    if (min > max) {
      const swap = min;
      min = max;
      max = swap;
    }
    setAgeError(null);
    const next = applyPlayerSetup(state, {
      gender,
      drawnTo: drawnTo.length > 0 ? drawnTo : null,
      name: initial.name,
      age,
      drawnToAgeMin: min,
      drawnToAgeMax: max,
    });
    onSave(next);
    onClose();
  }

  return (
    <div style={backdrop} role="dialog" aria-modal="true">
      <div style={card}>
        <header style={cardHeader}>
          <h2 style={titleStyle}>{t("setup:identityEdit.title")}</h2>
          <button
            type="button"
            style={closeBtn}
            onClick={onClose}
            aria-label={t("common:buttons.close")}
          >
            ×
          </button>
        </header>

        <div style={body}>
          <section>
            <div style={sectionLabel}>{t("setup:characterSetup.youAreLabel")}</div>
            <div style={pillRow}>
              {GENDERS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  style={pillStyle(gender === g.value)}
                  onClick={() => setGender(gender === g.value ? null : g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div style={hint}>{t("setup:characterSetup.youAreHint")}</div>
          </section>

          <section>
            <div style={sectionLabel}>{t("setup:characterSetup.drawnToLabel")}</div>
            <div style={pillRow}>
              {GENDERS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  style={pillStyle(drawnTo.includes(g.value))}
                  onClick={() => toggleDrawnTo(g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div style={hint}>{t("setup:characterSetup.drawnToHint")}</div>
          </section>

          <section>
            <div style={sectionLabel}>{t("setup:characterSetup.yourAgeLabel")}</div>
            <div style={ageInputRow}>
              <input
                type="number"
                min={AGE_MIN}
                max={AGE_MAX}
                step={1}
                inputMode="numeric"
                value={ageText}
                onChange={(e) => {
                  setAgeText(e.target.value);
                  if (ageError) setAgeError(null);
                }}
                placeholder={t("setup:characterSetup.agePlaceholder")}
                style={ageInputStyle}
                aria-label={t("setup:characterSetup.ageAriaLabel")}
              />
              <span style={ageUnit}>{t("setup:characterSetup.yearsUnit")}</span>
            </div>
            <div style={hint}>{t("setup:characterSetup.yourAgeHintEdit")}</div>
            {ageError && <div style={errorHint}>{ageError}</div>}
          </section>

          <section>
            <div style={sectionLabel}>{t("setup:characterSetup.agePartnersLabel")}</div>
            <div style={ageRangeRow}>
              <input
                type="number"
                min={AGE_MIN}
                max={AGE_MAX}
                step={1}
                inputMode="numeric"
                value={drawnToAgeMin}
                onChange={(e) =>
                  setDrawnToAgeMin(clamp(Number(e.target.value) || AGE_MIN))
                }
                style={ageInputStyle}
                aria-label={t("setup:characterSetup.agePartnersMinAria")}
              />
              <span style={ageRangeSep}>{t("setup:characterSetup.ageRangeSeparator")}</span>
              <input
                type="number"
                min={AGE_MIN}
                max={AGE_MAX}
                step={1}
                inputMode="numeric"
                value={drawnToAgeMax}
                onChange={(e) =>
                  setDrawnToAgeMax(clamp(Number(e.target.value) || AGE_MAX))
                }
                style={ageInputStyle}
                aria-label={t("setup:characterSetup.agePartnersMaxAria")}
              />
            </div>
            <div style={hint}>{t("setup:characterSetup.agePartnersHint")}</div>
          </section>

          <div style={actionsRow}>
            <button type="button" style={primaryAction} onClick={handleSave}>
              {t("common:buttons.save")}
            </button>
            <button type="button" style={secondaryAction} onClick={onClose}>
              {t("common:buttons.cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles (match AccountSettingsPage visual family) ──────────────────

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(8, 6, 4, 0.78)",
  zIndex: 200,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 16px",
  overflowY: "auto",
};

const card: CSSProperties = {
  background: "var(--paper, rgba(20,14,10,0.92))",
  color: "var(--ink, #e8d9b8)",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  borderRadius: 2,
  maxWidth: 560,
  width: "100%",
  padding: 0,
  boxShadow: "0 28px 70px rgba(0,0,0,0.7)",
  fontFamily: "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
};

const cardHeader: CSSProperties = {
  padding: "20px 28px 14px",
  borderBottom: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: "1.4rem",
  fontWeight: 500,
  color: "var(--accent, #d4a574)",
  letterSpacing: "0.02em",
};

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  color: "var(--ink-muted, #8a7355)",
  fontSize: "1.4rem",
  width: 32,
  height: 32,
  borderRadius: 2,
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1,
};

const body: CSSProperties = {
  padding: "20px 28px 24px",
  display: "flex",
  flexDirection: "column",
  gap: 22,
};

const sectionLabel: CSSProperties = {
  fontSize: "0.75rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "var(--ink-muted, #8a7355)",
  fontFamily: "'JetBrains Mono', monospace",
};

const pillRow: CSSProperties = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginTop: 8,
};

const hint: CSSProperties = {
  marginTop: 8,
  fontSize: "0.75rem",
  color: "var(--ink-muted, #7a6243)",
  fontStyle: "italic",
  fontFamily:
    "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
};

const errorHint: CSSProperties = {
  ...hint,
  color: "#e08585",
  fontStyle: "normal",
};

const ageInputRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 8,
};

const ageRangeRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 8,
};

const ageRangeSep: CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.85rem",
  color: "var(--ink-muted, #a89070)",
  letterSpacing: "0.05em",
};

const ageUnit: CSSProperties = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.85rem",
  color: "var(--ink-muted, #a89070)",
  letterSpacing: "0.05em",
};

const ageInputStyle: CSSProperties = {
  width: 80,
  padding: "8px 12px",
  background: "transparent",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.3))",
  borderRadius: 2,
  color: "var(--ink, #e8c896)",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.9rem",
  letterSpacing: "0.05em",
  outline: "none",
  textAlign: "center",
};

const actionsRow: CSSProperties = {
  display: "flex",
  gap: 10,
  marginTop: 8,
};

const primaryAction: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--accent, #d4a574)",
  color: "var(--accent, #d4a574)",
  padding: "10px 18px",
  fontSize: "0.85rem",
  borderRadius: 2,
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: "0.05em",
  cursor: "pointer",
  textTransform: "lowercase",
};

const secondaryAction: CSSProperties = {
  ...primaryAction,
  borderColor: "var(--paper-edge, rgba(212,165,116,0.3))",
  color: "var(--ink-muted, #a89070)",
};

function pillStyle(active: boolean): CSSProperties {
  return {
    padding: "8px 16px",
    background: active
      ? "var(--paper, rgba(212,165,116,0.15))"
      : "transparent",
    border: `1px solid ${
      active
        ? "var(--accent, #d4a574)"
        : "var(--paper-edge, rgba(212,165,116,0.3))"
    }`,
    borderRadius: 2,
    color: active
      ? "var(--ink, #e8c896)"
      : "var(--ink-muted, #a89070)",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.85rem",
    letterSpacing: "0.05em",
    cursor: "pointer",
    transition:
      "all 0.15s ease-out, background 0.4s ease-out, color 0.4s ease-out, border-color 0.4s ease-out",
    textTransform: "lowercase",
  };
}
